// src/components/TopBar.js
import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Box, Typography, Avatar, Menu, MenuItem, IconButton,
  Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CampaignIcon from '@mui/icons-material/Campaign';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HandshakeIcon from '@mui/icons-material/Handshake';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import logoImage from '../assets/logo.png';

import { getToken, parseJwt, logout as doLogout } from '../lib/auth';

const NavItem = styled(Box)(({ theme, $active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontSize: '0.82rem',
  fontWeight: $active ? 700 : 500,
  color: $active ? '#ffb71b' : '#666',
  backgroundColor: $active ? 'rgba(255, 183, 27, 0.08)' : 'transparent',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('lg')]: {
    padding: '8px 10px',
    fontSize: '0.78rem',
    gap: 4,
  },
  // Touch devices keep the hover styling stuck after a tap.
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: $active ? 'rgba(255, 183, 27, 0.12)' : '#f5f5f5',
      color: $active ? '#ffb71b' : '#333',
    },
  },
}));

const CreditsBadge = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 14px',
  borderRadius: 20,
  background: 'linear-gradient(135deg, #fff8e7 0%, #fff3d0 100%)',
  border: '1px solid #ffe082',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  '@media (hover: hover)': {
    '&:hover': {
      boxShadow: '0 2px 12px rgba(255, 183, 27, 0.25)',
      transform: 'translateY(-1px)',
    },
  },
}));

export default function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();

  const token = getToken();
  const payload = token ? parseJwt(token) : null;
  const userId = payload?.sub;
  const loggedIn = !!(payload?.exp && Date.now() < payload.exp * 1000);

  const [logo, setLogo] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [credits, setCredits] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/vendor/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load vendor data');

        const data = await res.json();
        if (data.vendor) {
          setBusinessName(data.vendor.company || '');
          setCredits(Number(data.vendor.credits) || 0);
        }
        if (data.vendor.logo) setLogo(data.vendor.logo);
      } catch (err) {
        doLogout();
        navigate('/login', { replace: true });
        handleMenuClose();
      }
    };

    fetchData();
    // Re-read on navigation: returning from the Credits page (or from Stripe)
    // should show the new balance rather than a stale one.
  }, [userId, location.pathname]);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const isActive = (base) => {
    const p = location.pathname.replace(/\/+$/, '');
    const b = base.replace(/\/+$/, '');
    if (b === '/dashboard' && (p === '/' || p.startsWith('/dashboard/'))) return true;
    return p === b || p.startsWith(`${b}/`);
  };

  const handleNav = (page) => {
    setDrawerOpen(false);
    if (loggedIn && userId) navigate(`/${page}/${userId}`);
    else navigate('/login');
  };

  const handleMenuItemClick = (page) => {
    if (page === 'logout') {
      doLogout();
      navigate('/login', { replace: true });
      handleMenuClose();
      return;
    }
    if (page === 'store-settings') {
      navigate(`/store-settings/${userId}`);
      handleMenuClose();
      return;
    }
    navigate(`/${page}`);
    handleMenuClose();
  };

  const logoPreview =
    logo instanceof File ? URL.createObjectURL(logo) : typeof logo === 'string' ? logo : null;

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
    { key: 'rewards', label: 'Rewards', icon: CardGiftcardIcon },
    { key: 'qr-code', label: 'QR Code', icon: QrCodeIcon },
    { key: 'notifications', label: 'Notifications', icon: NotificationsIcon },
    { key: 'collaborations', label: 'Collaborations', icon: HandshakeIcon },
    { key: 'campaigns', label: 'Campaigns', icon: CampaignIcon },
    { key: 'help', label: 'Help', icon: HelpOutlineIcon },
  ];

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          color: '#000',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 1.5, md: 2.5 },
            gap: 1,
          }}
        >
          {/* Left: menu button (mobile) + Logo + Nav (desktop) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 0.5, color: '#444' }}
            >
              <MenuIcon />
            </IconButton>

            <Box
              component="img"
              src={logoImage}
              alt="logo"
              onClick={() => handleNav('dashboard')}
              sx={{ width: { xs: 38, md: 44 }, cursor: 'pointer', flexShrink: 0 }}
            />

            {/* Full nav on desktop; the drawer takes over below md. */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                ml: 1.5,
                gap: 0.25,
              }}
            >
              {navItems.map(({ key, label, icon: Icon }) => (
                <NavItem
                  key={key}
                  $active={isActive(`/${key}`)}
                  onClick={() => handleNav(key)}
                >
                  <Icon sx={{ fontSize: 18 }} />
                  {label}
                </NavItem>
              ))}
            </Box>
          </Box>

          {/* Right: Credits + Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexShrink: 0 }}>
            {/* Credits Badge — drops to just the number on narrow screens */}
            <CreditsBadge onClick={() => handleNav('credits')} sx={{ px: { xs: 1.25, md: 1.75 } }}>
              <MonetizationOnIcon sx={{ fontSize: 18, color: '#ffb71b' }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: '#b8860b', fontSize: '0.82rem' }}
              >
                {credits === null ? '—' : credits.toLocaleString()}
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {credits === 1 ? ' credit' : ' credits'}
                </Box>
              </Typography>
            </CreditsBadge>

            {/* Profile */}
            <Box
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: 2,
                transition: 'background 0.2s',
                '&:hover': { backgroundColor: '#f9f9f9' },
              }}
            >
              <Avatar
                src={logoPreview}
                alt={businessName}
                sx={{
                  width: 34,
                  height: 34,
                  border: '2px solid #f0f0f0',
                }}
              />
              <Box sx={{ display: { xs: 'none', lg: 'block' }, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#222',
                    fontSize: '0.82rem',
                    lineHeight: 1.2,
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {businessName}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#999' }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  border: '1px solid #f0f0f0',
                  minWidth: 200,
                  p: 0.5,
                },
              }}
            >
              <MenuItem
                onClick={() => handleMenuItemClick('store-settings')}
                sx={{ borderRadius: 2, gap: 1.5, py: 1.25, fontSize: '0.9rem' }}
              >
                <SettingsIcon sx={{ fontSize: 18, color: '#888' }} />
                Store Settings
              </MenuItem>
              <Box sx={{ mx: 1.5, my: 0.5, borderTop: '1px solid #f0f0f0' }} />
              <MenuItem
                onClick={() => handleMenuItemClick('logout')}
                sx={{ borderRadius: 2, gap: 1.5, py: 1.25, fontSize: '0.9rem', color: '#e53935' }}
              >
                <LogoutIcon sx={{ fontSize: 18, color: '#e53935' }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile navigation. The seven nav items don't fit a phone width, so
          below md they move into a slide-out drawer instead of overflowing. */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 272, borderRadius: 0 } }}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Avatar src={logoPreview} alt={businessName} sx={{ width: 36, height: 36, border: '2px solid #f0f0f0' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {businessName || 'Your business'}
            </Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close navigation" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <List sx={{ px: 1, py: 1.5 }}>
          {navItems.map(({ key, label, icon: Icon }) => {
            const active = isActive(`/${key}`);
            return (
              <ListItemButton
                key={key}
                onClick={() => handleNav(key)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  // Comfortable touch target — the desktop nav rows are too small for a thumb.
                  minHeight: 46,
                  backgroundColor: active ? 'rgba(255, 183, 27, 0.10)' : 'transparent',
                }}
              >
                <ListItemIcon sx={{ minWidth: 38 }}>
                  <Icon sx={{ fontSize: 20, color: active ? '#ffb71b' : '#888' }} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{
                    fontSize: '0.92rem',
                    fontWeight: active ? 700 : 500,
                    color: active ? '#ffb71b' : '#444',
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>

        <Divider sx={{ mx: 2 }} />

        <List sx={{ px: 1, py: 1.5 }}>
          <ListItemButton
            onClick={() => { setDrawerOpen(false); navigate(`/store-settings/${userId}`); }}
            sx={{ borderRadius: 2, mb: 0.5, minHeight: 46 }}
          >
            <ListItemIcon sx={{ minWidth: 38 }}>
              <SettingsIcon sx={{ fontSize: 20, color: '#888' }} />
            </ListItemIcon>
            <ListItemText primary="Store Settings" primaryTypographyProps={{ fontSize: '0.92rem', color: '#444' }} />
          </ListItemButton>
          <ListItemButton
            onClick={() => { setDrawerOpen(false); doLogout(); navigate('/login', { replace: true }); }}
            sx={{ borderRadius: 2, minHeight: 46 }}
          >
            <ListItemIcon sx={{ minWidth: 38 }}>
              <LogoutIcon sx={{ fontSize: 20, color: '#e53935' }} />
            </ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.92rem', color: '#e53935' }} />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
}
